'use client';

import { API_ROUTES, useApiQuery } from '@shared/api-utils';
import type { LotteryName } from '../../../lottery-purchases/types';
import type { DrawingDatesQueryParams, DrawingDatesResponse } from '../../types';

type UseDrawingDatesOptions = {
  params?: DrawingDatesQueryParams;
  enabled?: boolean;
};

function getDrawingDatesPath(lotteryName: LotteryName): string {
  return API_ROUTES.RESULTS.DRAWING_DATES.replace(':lotteryName', lotteryName);
}

function toDrawingDatesApiParams(params?: DrawingDatesQueryParams): Record<string, string | number> | undefined {
  if (!params) return undefined;

  const apiParams: Record<string, string | number> = {};
  if (params.year !== undefined && params.year !== '') apiParams.year = params.year;
  if (params.month !== undefined && params.month !== '') apiParams.month = params.month;
  if (params.day !== undefined && params.day !== '') apiParams.day = params.day;
  if (params.timezone) apiParams.timezone = params.timezone;

  return Object.keys(apiParams).length > 0 ? apiParams : undefined;
}

/**
 * Fetches the compressed draw-date calendar for a lottery (LRS v2 `getDrawingDates`).
 * Use `params.year` / `month` / `day` to scope lower levels for archive dropdowns; `timezone` groups dates.
 */
function useDrawingDates(lotteryName: LotteryName, options: UseDrawingDatesOptions = {}) {
  const { params, enabled = true } = options;
  const path = getDrawingDatesPath(lotteryName);
  const apiParams = toDrawingDatesApiParams(params);

  return useApiQuery<DrawingDatesResponse>(path, {
    params: apiParams,
    queryOptions: {
      enabled: Boolean(lotteryName) && enabled,
      placeholderData: previousData => previousData,
    },
  });
}

export { getDrawingDatesPath, toDrawingDatesApiParams, useDrawingDates };
export type { UseDrawingDatesOptions };
